import http from '@/utils/http';
import { Watch, WikiAttachment, WikiCommentThread, WikiPage, WikiRevision, WikiSpace, WikiSpaceAccess } from './types';

// ---- pages ----
export const listWikiPages = () => http.get<WikiPage[]>('/wiki-pages').then((r) => r.data);
export const getWikiPage = (id: string) => http.get<WikiPage>(`/wiki-pages/${id}`).then((r) => r.data);
export const createWikiPage = (body: Partial<WikiPage>) => http.post<WikiPage>('/wiki-pages', body).then((r) => r.data);
/** Rename (title + optional icon). Narrow write — never clobbers a concurrent body edit or move. */
export const updateWikiPage = (id: string, body: { title?: string; icon?: string | null }) =>
  http.put<WikiPage>(`/wiki-pages/${id}`, body).then((r) => r.data);
/** Reparent / reorder in the tree (parent_id + position). Cycle-guarded server-side. */
export const moveWikiPage = (id: string, parentId: string | null, position: number) =>
  http.put<WikiPage>(`/wiki-pages/${id}/move`, { parent_id: parentId, position }).then((r) => r.data);
/** Persist the collaborative body: the ProseMirror-JSON snapshot, its plaintext, and the encoded Yjs
 *  document state (base64). The debounced autosave — no save button, like the rest of the app. */
export const saveWikiDoc = (id: string, body: { content: unknown; text: string; doc: string }) =>
  http.put<WikiPage>(`/wiki-pages/${id}/doc`, body).then((r) => r.data);
/** Soft-deletes the page + its sub-tree; returns the removed ids (for undo/restore). */
export const deleteWikiPage = (id: string) => http.delete<{ ids: string[] }>(`/wiki-pages/${id}`).then((r) => r.data);
export const restoreWikiPages = (ids: string[]) =>
  http.post<{ restored: number }>('/wiki-pages/restore', { ids }).then((r) => r.data);

// ---- revisions (history) ----
export const listWikiRevisions = (pageId: string) => http.get<WikiRevision[]>(`/wiki-pages/${pageId}/revisions`).then((r) => r.data);
export const getWikiRevision = (id: string) => http.get<WikiRevision>(`/wiki-revisions/${id}`).then((r) => r.data);

// ---- attachments ----
export const listWikiAttachments = (pageId: string) => http.get<WikiAttachment[]>(`/wiki-pages/${pageId}/attachments`).then((r) => r.data);
export const uploadWikiAttachment = (pageId: string, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return http.post<WikiAttachment>(`/wiki-pages/${pageId}/attachments`, fd).then((r) => r.data);
};
export const deleteWikiAttachment = (id: string) => http.delete(`/wiki-attachments/${id}`).then(() => undefined);
/** Direct stream URL — a plain <img>/<a> can't send the Bearer token (401s under OIDC); use
 *  {@link fetchWikiAttachmentBlob} for display. */
export const wikiAttachmentUrl = (id: string) => `${window.config.apiUrl}/wiki-attachments/${id}`;
export const fetchWikiAttachmentBlob = (id: string) =>
  http.get<Blob>(`/wiki-attachments/${id}`, { responseType: 'blob' }).then((r) => r.data);

// ---- spaces ----
// ---- item ↔ wiki-page references (derived from content on save) ----
export const listItemWikiPages = (itemId: string) =>
  http.get<import('./types').WikiPageRef[]>(`/items/${itemId}/wiki-pages`).then((r) => r.data);
export const listWikiPageItems = (pageId: string) =>
  http.get<import('./types').WikiItemRef[]>(`/wiki-pages/${pageId}/items`).then((r) => r.data);

export const listWikiSpaces = () => http.get<WikiSpace[]>('/wiki-spaces').then((r) => r.data);
export const getWikiSpaceBySlug = (slug: string) =>
  http.get<WikiSpace>(`/wiki-space-by-slug/${encodeURIComponent(slug)}`).then((r) => r.data);
export const getWikiSpace = (id: string) => http.get<WikiSpace>(`/wiki-spaces/${id}`).then((r) => r.data);
export const createWikiSpace = (body: Partial<WikiSpace>) =>
  http.post<WikiSpace>('/wiki-spaces', body).then((r) => r.data);
export const updateWikiSpace = (id: string, body: { name: string; icon?: string | null; description: string }) =>
  http.put<WikiSpace>(`/wiki-spaces/${id}`, body).then((r) => r.data);
export const deleteWikiSpace = (id: string) => http.delete(`/wiki-spaces/${id}`).then(() => undefined);
export const listWikiSpacePages = (spaceId: string) =>
  http.get<WikiPage[]>(`/wiki-spaces/${spaceId}/pages`).then((r) => r.data);
export const getWikiPageBySlug = (spaceId: string, slug: string) =>
  http.get<WikiPage>(`/wiki-spaces/${spaceId}/pages/by-slug/${encodeURIComponent(slug)}`).then((r) => r.data);
export const getWikiSpaceAccess = (spaceId: string) =>
  http.get<WikiSpaceAccess>(`/wiki-spaces/${spaceId}/access`).then((r) => r.data);
export const setWikiSpaceAccess = (spaceId: string, body: WikiSpaceAccess) =>
  http.put<WikiSpaceAccess>(`/wiki-spaces/${spaceId}/access`, body).then((r) => r.data);

// ---- watches (space/page) — reuse the generic /me/watches endpoint ----
export const listMyWatches = () => http.get<Watch[]>('/me/watches').then((r) => r.data);
export const setWatch = (targetType: 'wiki_space' | 'wiki_page', targetId: string, watching: boolean) =>
  http.put('/me/watches', { target_type: targetType, target_id: targetId, watching }).then(() => undefined);

// ---- comments (Google-Docs-style anchored threads) ----
export const listWikiComments = (pageId: string) =>
  http.get<WikiCommentThread[]>(`/wiki-pages/${pageId}/comments`).then((r) => r.data);
/** Open a thread with its first message. `id` is the client-minted thread id (== the mark anchor). */
export const createWikiThread = (pageId: string, body: { id: string; quote: string; body: string }) =>
  http.post<WikiCommentThread>(`/wiki-pages/${pageId}/comments`, body).then((r) => r.data);
export const addWikiComment = (threadId: string, body: string) =>
  http.post<WikiCommentThread>(`/wiki-comment-threads/${threadId}/comments`, { body }).then((r) => r.data);
export const updateWikiComment = (commentId: string, body: string) =>
  http.put<WikiCommentThread>(`/wiki-comments/${commentId}`, { body }).then((r) => r.data);
export const resolveWikiThread = (threadId: string, resolved: boolean) =>
  http.put<WikiCommentThread>(`/wiki-comment-threads/${threadId}/resolve`, { resolved }).then((r) => r.data);
export const deleteWikiThread = (threadId: string) =>
  http.delete<{ ids: string[] }>(`/wiki-comment-threads/${threadId}`).then((r) => r.data);
